import { useEffect, useState } from 'react';
import type { ApiClient, BillingEvent } from '../api';
import { formatDate, renderError } from './shared';

const STATUS_CLASS: Record<string, string> = { processed: 'active', failed: 'suspended', skipped: '', pending: 'past_due' };

/**
 * Every provider notification (PayU, mock) as it arrived and what became of
 * it. "Failed" ones are what to look at: fix the cause, then replay — the
 * stored payload is re-processed without asking the provider to resend.
 */
export function BillingEventsView({ client }: { client: ApiClient }) {
  const [status, setStatus] = useState('');
  const [data, setData] = useState<{ total: number; events: BillingEvent[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    client
      .listBillingEvents(status || undefined)
      .then((response) => !cancelled && setData(response))
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
  }, [client, status, tick]);

  async function replay(event: BillingEvent) {
    if (!window.confirm(`Re-process ${event.eventType} (${event.providerEventId})? The stored payload is applied again.`)) return;
    setBusy(event.id);
    setError(null);
    setResult(null);
    try {
      const outcome = await client.replayBillingEvent(event.id);
      setResult(`Replayed ${event.providerEventId}: ${outcome.outcome}${outcome.reason ? ` (${outcome.reason})` : ''}`);
      setTick((value) => value + 1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Billing</p>
          <h2>PayU events</h2>
          <p className="muted">Signed notifications from payment providers, newest first. Unsigned or forged ones never get this far.</p>
        </div>
      </div>

      <div className="toolbar">
        <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Processing status">
          <option value="">All events</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="skipped">Skipped</option>
          <option value="processed">Processed</option>
        </select>
        <button onClick={() => setTick((value) => value + 1)}>Refresh</button>
      </div>

      {error !== null && renderError(error)}
      {result && <div className="notice">{result}</div>}

      <div className="panel">
        {data === null ? (
          <p className="muted">Loading…</p>
        ) : data.events.length === 0 ? (
          <p className="muted">No events.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Received</th>
                <th>Provider</th>
                <th>Event</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.events.map((event) => (
                <tr key={event.id}>
                  <td>{formatDate(event.receivedAt)}</td>
                  <td>{event.provider}</td>
                  <td>{event.eventType}</td>
                  <td>
                    <code>{event.providerEventId}</code>
                  </td>
                  <td>
                    <span className={`tag ${STATUS_CLASS[event.processingStatus] ?? ''}`}>{event.processingStatus}</span>
                  </td>
                  <td>{event.attempts}</td>
                  <td className="muted small">{event.error ?? ''}</td>
                  <td>
                    {event.processingStatus !== 'processed' && (
                      <button disabled={busy !== null} onClick={() => void replay(event)}>
                        {busy === event.id ? 'Replaying…' : 'Replay'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && <p className="muted small">{data.total} total{data.total > data.events.length ? `, newest ${data.events.length} shown` : ''}</p>}
      </div>
    </>
  );
}
